import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, CalendarOff, CalendarClock, Power } from "lucide-react";
import { authFetch } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminLoading from "../../components/admin/AdminLoading";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import AdminTable, { AdminMobileCard, adminTableHeadClass, adminTableRowClass, adminClickableRowClass } from "../../components/admin/AdminTable";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import WeekdayCheckboxes from "../../components/admin/WeekdayCheckboxes";
import { sortSlots } from "../../components/PlanningSlots";
import { toast } from "sonner";
import {
  ADMIN_BADGE_OK,
  ADMIN_BADGE_OFF,
  ADMIN_CHIP,
  ADMIN_CHIP_ACTIVE,
  ADMIN_FIELD,
  ADMIN_LABEL,
  ADMIN_PANEL,
  ADMIN_SELECT,
  BTN_OUTLINE,
  BTN_PRIMARY,
} from "../../config/brand";
import { formatDaysLabelLong, normalizeDaysOfWeek } from "../../lib/weekdays";

const emptySlot = {
  center_id: "",
  offer_id: "",
  formula_id: "",
  stage_id: "",
  days_of_week: ["lundi"],
  start_time: "17:00",
  end_time: "18:30",
  start_date: "",
  end_date: "",
  max_capacity: 10,
};

const emptyClosure = {
  start_date: "",
  end_date: "",
  reason: "",
  center_id: "",
};

function slotDaysText(slot) {
  return (
    formatDaysLabelLong(slot.days_of_week || slot.day_of_week) ||
    slot.days_label ||
    slot.day_of_week ||
    "—"
  );
}

function slotDateText(slot) {
  if (!slot?.start_date) return "—";
  if (slot.end_date && slot.end_date !== slot.start_date) {
    return `${slot.start_date} → ${slot.end_date}`;
  }
  return slot.start_date;
}

function slotPublishHint(slot, offers = []) {
  const offer = offers.find((o) => o.id === slot.offer_id);
  if (offer?.category !== "stage" && slot.offer_category !== "stage") return null;
  if (slot.active === false) return "Inactif — retiré de la réservation";
  if (!slot.start_date) return "Non publié — date de début manquante";
  if (!slot.formula_id) return "Non publié sous une formule — choisissez Demi-journée / Journée / Semaine";
  if (!slot.stage_id) return "Publié en réservation — thème optionnel non lié";
  return null;
}

export default function AdminSlots() {
  const [tab, setTab] = useState("slots");
  const [slots, setSlots] = useState([]);
  const [closures, setClosures] = useState([]);
  const [centers, setCenters] = useState([]);
  const [offers, setOffers] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [slotDialog, setSlotDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [slotForm, setSlotForm] = useState(emptySlot);
  const [slotErrors, setSlotErrors] = useState({});

  const [closureDialog, setClosureDialog] = useState(false);
  const [editingClosure, setEditingClosure] = useState(null);
  const [closureForm, setClosureForm] = useState(emptyClosure);

  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [slotsRes, closuresRes, centersRes, offersRes, stagesRes] = await Promise.all([
        authFetch("/api/admin/schedules"),
        authFetch("/api/admin/closures"),
        authFetch("/api/admin/centers"),
        authFetch("/api/admin/offers"),
        authFetch("/api/admin/stages"),
      ]);
      if (slotsRes.ok) setSlots(await slotsRes.json());
      if (closuresRes.ok) setClosures(await closuresRes.json());
      if (centersRes.ok) setCenters(await centersRes.json());
      if (offersRes.ok) setOffers(await offersRes.json());
      if (stagesRes.ok) setStages(await stagesRes.json());
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const centerName = (id) => centers.find((c) => c.id === id)?.name || "-";
  const sortedSlots = sortSlots(slots);
  const selectedOffer = offers.find((o) => o.id === slotForm.offer_id);
  const isStageOffer = selectedOffer?.category === "stage";

  const openCreateSlot = () => {
    setEditingSlot(null);
    setSlotForm(emptySlot);
    setSlotErrors({});
    setSlotDialog(true);
  };

  const openEditSlot = (slot) => {
    const days = normalizeDaysOfWeek(slot.days_of_week || slot.day_of_week);
    setEditingSlot(slot);
    setSlotForm({
      center_id: slot.center_id || "",
      offer_id: slot.offer_id || "",
      formula_id: slot.formula_id || "",
      stage_id: slot.stage_id || "",
      days_of_week: days.length ? days : ["lundi"],
      start_time: slot.start_time,
      end_time: slot.end_time,
      start_date: slot.start_date || "",
      end_date: slot.end_date || "",
      max_capacity: slot.max_capacity,
    });
    setSlotErrors({});
    setSlotDialog(true);
  };

  const validateSlot = () => {
    const next = {};
    if (!slotForm.center_id) next.center_id = "Centre requis";
    if (!slotForm.days_of_week?.length) next.days_of_week = "Cochez au moins un jour";
    if (isStageOffer) {
      if (!slotForm.start_date) next.start_date = "Date de début requise pour un stage";
      if (!slotForm.formula_id) next.formula_id = "Choisissez la formule (demi-journée, journée ou semaine)";
    }
    setSlotErrors(next);
    return Object.keys(next).length === 0;
  };

  const saveSlot = async () => {
    if (!validateSlot()) return;
    setSaving(true);
    const payload = {
      center_id: slotForm.center_id,
      offer_id: slotForm.offer_id || null,
      formula_id: slotForm.formula_id || null,
      stage_id: slotForm.stage_id || null,
      days_of_week: slotForm.days_of_week,
      day_of_week: slotForm.days_of_week[0],
      start_time: slotForm.start_time,
      end_time: slotForm.end_time,
      start_date: slotForm.start_date || null,
      end_date: slotForm.end_date || slotForm.start_date || null,
      max_capacity: Number(slotForm.max_capacity) || 10,
    };
    try {
      const url = editingSlot
        ? `/api/admin/schedules/${editingSlot.id}`
        : "/api/admin/schedules";
      const res = await authFetch(url, {
        method: editingSlot ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editingSlot ? "Créneau mis à jour" : "Créneau créé");
      setSlotDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (slot) => {
    setTogglingId(slot.id);
    const nextActive = slot.active === false;
    try {
      const res = await authFetch(`/api/admin/schedules/${slot.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active: nextActive }),
      });
      if (!res.ok) throw new Error("Erreur");
      toast.success(
        nextActive
          ? "Créneau activé"
          : "Créneau désactivé — retiré des réservations, inscriptions conservées"
      );
      loadAll();
    } catch {
      toast.error("Impossible de changer le statut");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === "slot") {
        const res = await authFetch(`/api/admin/schedules/${deleteTarget.id}`, { method: "DELETE" });
        if (!res.ok) throw new Error("Erreur");
        toast.success("Créneau supprimé. Les inscriptions existantes sont conservées.");
      } else {
        const res = await authFetch(`/api/admin/closures/${deleteTarget.id}`, { method: "DELETE" });
        if (!res.ok) throw new Error("Erreur");
        toast.success("Fermeture supprimée");
      }
      setDeleteTarget(null);
      loadAll();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(false);
    }
  };

  const openCreateClosure = () => {
    setEditingClosure(null);
    setClosureForm(emptyClosure);
    setClosureDialog(true);
  };

  const openEditClosure = (closure) => {
    setEditingClosure(closure);
    setClosureForm({
      start_date: closure.start_date || "",
      end_date: closure.end_date || "",
      reason: closure.reason || "",
      center_id: closure.center_ids?.[0] || "",
    });
    setClosureDialog(true);
  };

  const saveClosure = async () => {
    if (!closureForm.start_date || !closureForm.end_date || !closureForm.reason) {
      toast.error("Dates et raison requises");
      return;
    }
    setSaving(true);
    const payload = {
      start_date: closureForm.start_date,
      end_date: closureForm.end_date,
      reason: closureForm.reason,
      center_ids: closureForm.center_id ? [closureForm.center_id] : null,
    };
    try {
      const url = editingClosure
        ? `/api/admin/closures/${editingClosure.id}`
        : "/api/admin/closures";
      const res = await authFetch(url, {
        method: editingClosure ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success(editingClosure ? "Fermeture mise à jour" : "Fermeture créée");
      setClosureDialog(false);
      loadAll();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminPageHeader
        icon={CalendarClock}
        title="Slots & Disponibilités"
        description="Source de vérité des dates, jours et horaires. Pour un stage : dates + formule (+ thème optionnel). Désactiver retire le créneau du site."
        action={
          tab === "slots" ? (
            <Button onClick={openCreateSlot} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau créneau
            </Button>
          ) : (
            <Button onClick={openCreateClosure} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle fermeture
            </Button>
          )
        }
      />

      <div className="flex gap-2 mb-6 flex-wrap" role="group" aria-label="Type de disponibilité">
        <button
          type="button"
          onClick={() => setTab("slots")}
          aria-pressed={tab === "slots"}
          className={tab === "slots" ? ADMIN_CHIP_ACTIVE : ADMIN_CHIP}
        >
          Créneaux ({slots.length})
        </button>
        <button
          type="button"
          onClick={() => setTab("closures")}
          aria-pressed={tab === "closures"}
          className={tab === "closures" ? ADMIN_CHIP_ACTIVE : ADMIN_CHIP}
        >
          Fermetures ({closures.length})
        </button>
      </div>

      {loading ? (
        <AdminLoading message="Chargement des créneaux..." />
      ) : tab === "slots" ? (
        slots.length === 0 ? (
          <AdminEmptyState
            icon={CalendarClock}
            title="Aucun créneau"
            description="Ajoutez un créneau hebdomadaire pour permettre les réservations."
            action={
              <Button onClick={openCreateSlot} className={BTN_PRIMARY}>
                <Plus className="h-4 w-4 mr-2" />
                Nouveau créneau
              </Button>
            }
          />
        ) : (
        <AdminTable
          mobileCards={sortedSlots.map((s) => (
            <AdminMobileCard
              key={s.id}
              title={s.center_name || centerName(s.center_id)}
              subtitle={
                s.offer_name
                  ? `${s.offer_name}${s.formula_name ? ` / ${s.formula_name}` : ""}`
                  : s.workshop_name || "—"
              }
              onClick={() => openEditSlot(s)}
              badge={
                <Badge className={s.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                  {s.active !== false ? "Actif" : "Inactif"}
                </Badge>
              }
              meta={[
                { label: "Date", value: slotDateText(s) },
                { label: "Jours", value: slotDaysText(s) },
                { label: "Stage", value: s.stage_name || "—" },
                { label: "Horaire", value: `${s.start_time} - ${s.end_time}` },
                { label: "Places", value: `${s.current_bookings}/${s.max_capacity}` },
                ...(slotPublishHint(s, offers)
                  ? [{ label: "Publication", value: slotPublishHint(s, offers) }]
                  : []),
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEditSlot(s)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(s)}
                    disabled={togglingId === s.id}
                    className={s.active === false ? "text-emerald-600" : "text-amber-600"}
                  >
                    <Power className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() =>
                      setDeleteTarget({
                        type: "slot",
                        id: s.id,
                        label: `${slotDaysText(s)} ${s.start_time}`,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table className="min-w-[1100px]">
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={`${adminTableHeadClass} min-w-[7rem]`}>Centre</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[10rem]`}>Offre / Formule</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[8rem]`}>Stage</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[11rem]`}>Date</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[8rem]`}>Jours</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[7rem]`}>Horaire</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[4rem]`}>Places</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[6rem]`}>Statut</TableHead>
                <TableHead className={`${adminTableHeadClass} min-w-[10rem] text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSlots.map((s) => {
                const publishHint = slotPublishHint(s, offers);
                return (
                  <TableRow
                    key={s.id}
                    className={adminClickableRowClass}
                    onClick={() => openEditSlot(s)}
                  >
                    <TableCell className="text-slate-900">{s.center_name || centerName(s.center_id)}</TableCell>
                    <TableCell className={ADMIN_LABEL}>
                      {s.offer_name ? (
                        <span>
                          {s.offer_name}
                          {s.formula_name ? <span className="text-slate-500"> / {s.formula_name}</span> : null}
                        </span>
                      ) : (
                        <span className="text-slate-500">{s.workshop_name || "-"}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-700 text-sm">{s.stage_name || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm whitespace-nowrap" title={slotDateText(s)}>
                      {slotDateText(s)}
                    </TableCell>
                    <TableCell
                      className="text-slate-700 capitalize text-sm whitespace-nowrap bg-inherit"
                      title={slotDaysText(s)}
                    >
                      {slotDaysText(s)}
                    </TableCell>
                    <TableCell className={`${ADMIN_LABEL} tabular-nums whitespace-nowrap`}>
                      {s.start_time} - {s.end_time}
                    </TableCell>
                    <TableCell className="text-slate-700 tabular-nums whitespace-nowrap">
                      {s.current_bookings}/{s.max_capacity}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 items-start">
                        <Badge className={s.active !== false ? ADMIN_BADGE_OK : ADMIN_BADGE_OFF}>
                          {s.active !== false ? "Actif" : "Inactif"}
                        </Badge>
                        {publishHint ? (
                          <span className="text-[10px] leading-tight text-amber-700 max-w-[10rem]" title={publishHint}>
                            {publishHint}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEditSlot(s)} aria-label="Modifier">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(s)}
                        disabled={togglingId === s.id}
                        aria-label={s.active === false ? "Activer" : "Désactiver"}
                        className={s.active === false ? "text-emerald-600" : "text-amber-600"}
                      >
                        {togglingId === s.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-1" />
                            {s.active === false ? "Activer" : "Désactiver"}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() =>
                          setDeleteTarget({
                            type: "slot",
                            id: s.id,
                            label: `${slotDaysText(s)} ${s.start_time}`,
                          })
                        }
                        aria-label="Supprimer"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </AdminTable>
        )
      ) : closures.length === 0 ? (
        <AdminEmptyState
          icon={CalendarOff}
          title="Aucune fermeture"
          description="Planifiez une fermeture exceptionnelle pour bloquer les réservations."
          action={
            <Button onClick={openCreateClosure} className={BTN_PRIMARY}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle fermeture
            </Button>
          }
        />
      ) : (
        <AdminTable
          mobileCards={closures.map((c) => (
            <AdminMobileCard
              key={c.id}
              title={`${c.start_date} → ${c.end_date}`}
              subtitle={c.reason}
              onClick={() => openEditClosure(c)}
              meta={[
                { label: "Centre", value: c.center_ids?.length ? centerName(c.center_ids[0]) : "Tous" },
              ]}
              actions={
                <>
                  <Button variant="ghost" size="sm" onClick={() => openEditClosure(c)} aria-label="Modifier">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => setDeleteTarget({ type: "closure", id: c.id, label: c.reason })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              }
            />
          ))}
        >
          <Table>
            <TableHeader>
              <TableRow className={`${adminTableRowClass} hover:bg-transparent`}>
                <TableHead className={adminTableHeadClass}>Période</TableHead>
                <TableHead className={adminTableHeadClass}>Raison</TableHead>
                <TableHead className={adminTableHeadClass}>Centre</TableHead>
                <TableHead className={`${adminTableHeadClass} text-right`}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {closures.map((c) => (
                  <TableRow
                    key={c.id}
                    className={adminClickableRowClass}
                    onClick={() => openEditClosure(c)}
                  >
                    <TableCell className="text-slate-900">
                      <span className="flex items-center gap-2">
                        <CalendarOff className="h-4 w-4 text-[#3a00e0]" />
                        {c.start_date} → {c.end_date}
                      </span>
                    </TableCell>
                    <TableCell className={ADMIN_LABEL}>{c.reason}</TableCell>
                    <TableCell className={ADMIN_LABEL}>
                      {c.center_ids?.length ? centerName(c.center_ids[0]) : "Tous"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEditClosure(c)} aria-label="Modifier">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteTarget({ type: "closure", id: c.id, label: c.reason })}
                        aria-label="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
        </AdminTable>
      )}

      <Dialog open={slotDialog} onOpenChange={setSlotDialog}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>{editingSlot ? "Modifier le créneau" : "Nouveau créneau"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="slot-center">Centre</Label>
              <select
                id="slot-center"
                value={slotForm.center_id}
                onChange={(e) => setSlotForm({ ...slotForm, center_id: e.target.value })}
                className={`${ADMIN_SELECT}`}
              >
                <option value="">-- Choisir --</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {slotErrors.center_id ? (
                <p className="text-red-600 text-xs" role="alert">{slotErrors.center_id}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slot-offer">Offre</Label>
                <select
                  id="slot-offer"
                  value={slotForm.offer_id}
                  onChange={(e) =>
                    setSlotForm({
                      ...slotForm,
                      offer_id: e.target.value,
                      formula_id: "",
                      stage_id:
                        offers.find((o) => o.id === e.target.value)?.category === "stage"
                          ? slotForm.stage_id
                          : "",
                    })
                  }
                  className={`${ADMIN_SELECT}`}
                >
                  <option value="">Aucune</option>
                  {offers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="slot-formula">Formule{isStageOffer ? " *" : ""}</Label>
                <select
                  id="slot-formula"
                  value={slotForm.formula_id}
                  onChange={(e) => {
                    setSlotForm({ ...slotForm, formula_id: e.target.value });
                    setSlotErrors((prev) => {
                      const next = { ...prev };
                      if (e.target.value) delete next.formula_id;
                      return next;
                    });
                  }}
                  disabled={!selectedOffer}
                  className={`${ADMIN_SELECT} disabled:opacity-50`}
                >
                  <option value="">Aucune</option>
                  {(selectedOffer?.formulas || []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                {slotErrors.formula_id ? (
                  <p className="text-red-600 text-xs" role="alert">{slotErrors.formula_id}</p>
                ) : null}
              </div>
            </div>
            {isStageOffer ? (
              <div className="space-y-2">
                <Label htmlFor="slot-stage">Stage thématique (optionnel)</Label>
                <select
                  id="slot-stage"
                  value={slotForm.stage_id}
                  onChange={(e) => setSlotForm({ ...slotForm, stage_id: e.target.value })}
                  className={`${ADMIN_SELECT}`}
                >
                  <option value="">— Aucun (offre Stage Vacances seule) —</option>
                  {stages
                    .filter((s) => s.active !== false)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.theme ? ` (${s.theme})` : ""}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-slate-500">
                  Sans thème lié, le créneau apparaît quand même à l’étape Créneau de la réservation
                  (formule + dates). Liez un thème pour l’afficher aussi sur la fiche stage publique.
                </p>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slot-start-date">Date début{isStageOffer ? " *" : ""}</Label>
                <Input
                  id="slot-start-date"
                  type="date"
                  value={slotForm.start_date}
                  onChange={(e) => setSlotForm({ ...slotForm, start_date: e.target.value })}
                  className={ADMIN_FIELD}
                />
                {slotErrors.start_date ? (
                  <p className="text-red-600 text-xs" role="alert">{slotErrors.start_date}</p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="slot-end-date">Date fin</Label>
                <Input
                  id="slot-end-date"
                  type="date"
                  value={slotForm.end_date}
                  onChange={(e) => setSlotForm({ ...slotForm, end_date: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <WeekdayCheckboxes
              idPrefix="slot-day"
              legend="Jours *"
              value={slotForm.days_of_week}
              onChange={(days) => {
                setSlotForm({ ...slotForm, days_of_week: days });
                setSlotErrors((prev) => {
                  const next = { ...prev };
                  if (days.length) delete next.days_of_week;
                  else next.days_of_week = "Cochez au moins un jour";
                  return next;
                });
              }}
              error={slotErrors.days_of_week}
              hint={
                <p>
                  Sélection multiple (lundi–dimanche). Pour un stage, ces jours sont ceux publiés sur le site.
                </p>
              }
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slot-start">Début</Label>
                <Input
                  id="slot-start"
                  type="time"
                  value={slotForm.start_time}
                  onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slot-end">Fin</Label>
                <Input
                  id="slot-end"
                  type="time"
                  value={slotForm.end_time}
                  onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="slot-capacity">Capacité (places)</Label>
              <Input
                id="slot-capacity"
                type="number"
                value={slotForm.max_capacity}
                onChange={(e) => setSlotForm({ ...slotForm, max_capacity: e.target.value })}
                className={ADMIN_FIELD}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlotDialog(false)} className={BTN_OUTLINE}>
              Annuler
            </Button>
            <Button
              onClick={saveSlot}
              disabled={saving || !slotForm.days_of_week?.length}
              className={BTN_PRIMARY}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closureDialog} onOpenChange={setClosureDialog}>
        <DialogContent className={`${ADMIN_PANEL} text-slate-900 max-w-lg`}>
          <DialogHeader>
            <DialogTitle>{editingClosure ? "Modifier la fermeture" : "Nouvelle fermeture"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="closure-start">Date début</Label>
                <Input
                  id="closure-start"
                  type="date"
                  value={closureForm.start_date}
                  onChange={(e) => setClosureForm({ ...closureForm, start_date: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="closure-end">Date fin</Label>
                <Input
                  id="closure-end"
                  type="date"
                  value={closureForm.end_date}
                  onChange={(e) => setClosureForm({ ...closureForm, end_date: e.target.value })}
                  className={ADMIN_FIELD}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="closure-reason">Raison</Label>
              <Input
                id="closure-reason"
                value={closureForm.reason}
                onChange={(e) => setClosureForm({ ...closureForm, reason: e.target.value })}
                placeholder="Vacances, jour férié..."
                className={ADMIN_FIELD}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closure-center">Centre (vide = tous)</Label>
              <select
                id="closure-center"
                value={closureForm.center_id}
                onChange={(e) => setClosureForm({ ...closureForm, center_id: e.target.value })}
                className={`${ADMIN_SELECT}`}
              >
                <option value="">Tous les centres</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setClosureDialog(false)}
              className={BTN_OUTLINE}
            >
              Annuler
            </Button>
            <Button onClick={saveClosure} disabled={saving} className={BTN_PRIMARY}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
        title={deleteTarget?.type === "closure" ? "Supprimer cette fermeture ?" : "Supprimer ce créneau ?"}
        description={
          deleteTarget?.type === "closure"
            ? `La fermeture « ${deleteTarget?.label} » sera définitivement supprimée.`
            : `Le créneau du ${deleteTarget?.label} sera définitivement retiré. Les inscriptions déjà enregistrées sont conservées.`
        }
        confirmLabel="Supprimer"
        onConfirm={confirmDelete}
        confirming={deleting}
      />
    </div>
  );
}
